import React, { useState } from "react";
import { Grid, Segment, List, Label, Icon, Input } from "semantic-ui-react";
import { HeaderPage } from "../../components/Admin";
import { useAuth } from "../../hooks";
import { canCharge } from "../../utils/constants";

// Guía breve de uso. Al agregar una función que cambie el flujo de alguno de
// estos roles, actualizar su lista acá.
// Mismas opciones, íconos y orden que el menú ☰ (MENU en SideMenu.js).
const menuItems = [
  {
    icon: "home",
    title: "Pedidos",
    description:
      "Las mesas del salón y su estado. Si el local tiene varios ambientes (Salón, Terraza…), arriba hay una pestaña por cada uno, con las mesas ocupadas y un aviso morado si hay platos listos para llevar; «Todos» los muestra juntos con su título.",
  },
  {
    icon: "motorcycle",
    title: "Venta rápida y delivery",
    description: "Pedidos para llevar, por WhatsApp y delivery. El número indica cuántos hay abiertos.",
  },
  {
    icon: "food",
    title: "Cocina",
    description: "La pantalla de cada área (Cocina, Bar…) con los pedidos por preparar.",
  },
  {
    icon: "bell outline",
    title: "Listos para servir",
    description: "Platos y bebidas que ya están listos y faltan llevar a la mesa, con cuánto llevan esperando.",
  },
  {
    icon: "calculator",
    title: "Caja",
    description:
      "Aquí se cobra: en Cobrar ves todas las mesas con consumo (Salón, Rápida y Delivery) y su cuenta al lado, sin entrar mesa por mesa. También la apertura y cierre del turno y el historial de cierres. Para el cajero y el administrador.",
  },
  {
    icon: "chart bar",
    title: "Reportes",
    description:
      "Panel del día, historial de pagos, resumen diario, ventas por producto, horas pico, tiempos de cocina, pérdidas, anulaciones y descuentos. Solo para el administrador.",
  },
  {
    icon: "book",
    title: "Carta",
    description:
      "Aquí se crean las categorías y los productos, y se organiza cómo los ve el cliente en el menú: el orden, las fotos, qué está activo y qué no. Solo para el administrador.",
  },
  {
    icon: "setting",
    title: "Configuración",
    description:
      "Aquí se crean las mesas del salón (y las de venta rápida y delivery), los clientes y los usuarios del sistema; se registran los QR de Yape y Plin para cobrar, el logo y nombre del negocio, la regla de las propinas, y se descarga el respaldo. Solo para el administrador.",
  },
  {
    icon: "question circle outline",
    title: "Ayuda",
    description: "Esta guía.",
  },
];

const clientSteps = [
  {
    title: "Escanea el QR de la mesa",
    description:
      "No necesita crear cuenta ni descargar nada. Si no puede escanear, escribe solo el código impreso debajo del QR.",
  },
  {
    title: "Arma su pedido",
    description:
      "Elige cantidad y opciones del plato, y una nota para cocina si quiere.",
  },
  {
    title: "Confirma el carrito",
    description: "El pedido llega directo a la pantalla de cocina de cada área.",
  },
  {
    title: "Sigue sus pedidos",
    description:
      'Se actualiza solo: "En marcha" → "¡Listo! Ya te lo llevan" → "Entregado".',
  },
  {
    title: "Pide la cuenta",
    description:
      "Cuando todo está entregado. Elige efectivo, tarjeta, Yape, Plin o Mixto (una parte en una forma y el resto en otra). La cobra la Caja.",
  },
];

// Quién ve qué: mismo criterio que el menú (SideMenu.js) y el backend
// (users/permissions.py). El mozo atiende; la Caja cobra y cierra la mesa.
const roles = [
  {
    icon: "user",
    title: "Mozo",
    who: "Usuario Estándar",
    sees: "Pedidos, Venta rápida y delivery, Cocina, Listos para servir y Ayuda.",
    does: "Atiende: abre la mesa, toma pedidos, los entrega y genera la cuenta. No cobra ni cierra la mesa.",
  },
  {
    icon: "calculator",
    title: "Cajero",
    who: 'Usuario Estándar con "Puede cobrar (caja)"',
    sees: "Lo mismo que el mozo, más Caja.",
    does: "Cobra todo (salón, venta rápida, WhatsApp y delivery), aplica descuentos, libera mesas y abre y cierra la caja.",
  },
  {
    icon: "user secret",
    title: "Administrador",
    who: "Usuario Admin",
    sees: "Todo el menú: además Reportes, Carta y Configuración.",
    does: "Cobra como el cajero, administra la carta, las mesas, los clientes y los usuarios, y ve los reportes. Puede anular ventas.",
  },
];

const workerSteps = [
  {
    title: "Al abrir una mesa libre, marca cuántas personas son y quién atiende",
    description:
      'Toca la mesa en Pedidos, elige el número de personas, revisa el nombre del Mesero (viene con tu nombre; cámbialo si otra persona atiende) y "Continuar": entras directo a añadir el pedido. Ahí mismo puedes "Seleccionar un cliente" (o crearlo). Dentro de la mesa se corrigen las personas y el cliente.',
  },
  {
    title: "Mira el color de cada mesa",
    description: "Así sabes cuál necesita atención sin entrar una por una.",
  },
  {
    title: "Añade pedidos si el cliente lo pide",
    description:
      'Dentro de la mesa, "Añadir pedido". El botón "Ver carrito" muestra cuántos platos llevas. Queda registrado a tu nombre. En el carrito, cada plato lleva su "Nota para cocina" (ej. "sin ají") y puede ir "Para llevar" (cocina lo empaca; ya viene encendido en WhatsApp, venta rápida y delivery) o como "Cortesía" (gratis, con motivo).',
  },
  {
    title: "Mesa morada o 🔔 arriba: hay platos listos",
    description:
      'Cocina ya los terminó (suena un aviso). Toca la campana (o abre "Listos para servir" en el menú, donde se ve cuánto lleva esperando cada mesa) para ver a cuál llevarlos y márcalos "Entregado" (solo puedes entregar lo que cocina o el bar ya marcó "Listo"). Un plato pendiente se puede cancelar (eligiendo el motivo); uno listo ya no.',
  },
  {
    title: "Genera la cuenta cuando el cliente la pida",
    description:
      'Dentro de la mesa, "Generar cuenta" (también puedes imprimir la pre-cuenta: es para entregar al cliente y que verifique lo consumido; todavía no es el comprobante de pago). Tú no cobras ni cierras la mesa: lo hace la Caja. Desde la mesa, "Ver cuenta" solo muestra el total y dice "Esta cuenta se cobra en Caja".',
  },
  {
    title: "¿Dejaron propina? Anótala en la cuenta",
    description:
      'Si el restaurante usa propinas, la cuenta tiene "Propina en efectivo" y "Propina en tarjeta": escribe lo que dejó el cliente antes de que la Caja cobre (se guarda al salir del campo). No se suma a la cuenta ni lleva IGV. La Caja la reparte al final del turno.',
  },
  {
    title: "Venta rápida, WhatsApp o delivery",
    description:
      'Menú ☰ → "Venta rápida y delivery" (el número al lado indica cuántos hay abiertos): mesas virtuales ("Venta rápida 1", "WhatsApp 1", "Delivery 1"…) con el mismo flujo que una mesa. Venta rápida = pidió en el mostrador para llevar; WhatsApp = llegó por mensaje y recoge; Delivery = se envía: al abrirla eliges el canal (PedidosYa, Rappi, Teléfono…) y el costo de envío, que se suma a la cuenta. En estas mesas el pedido sale en un solo lote (una bolsa): cuando cocina y bar marcan todo "Listo", aparece "Entregar todo"; no se entrega plato por plato.',
  },
  {
    title: "¿Se fueron sin pagar?",
    description:
      'Avisa a la Caja: en Caja → Cobrar, "Liberar mesa" registra lo servido o preparado como pérdida y deja la mesa libre.',
  },
];

const cashierSteps = [
  {
    title: "Abre la caja al empezar el turno",
    description:
      "Caja → Apertura y cierre, con el efectivo inicial. Durante el turno, en «Ingresos/Egresos Varios», registras el efectivo que entra o sale del cajón fuera de las ventas (con motivo), por ejemplo sencillo o la compra de hielo. Las ventas se suman solas: no las registres ahí, o se contarían dos veces. Sin caja abierta no se puede cobrar: el botón dice «Abrir Caja antes de proceder al cobro».",
  },
  {
    title: "Caja → Cobrar: todo lo que falta cobrar",
    description:
      'Pestañas Salón, Rápida (venta rápida y WhatsApp) y Delivery, con cuántas tiene cada una. Cada tarjeta muestra los pedidos, el total y desde hace cuánto; en verde, las que ya pidieron la cuenta, con la forma de pago que avisó el cliente.',
  },
  {
    title: "Toca la mesa y cobra",
    description:
      'Su cuenta aparece al lado. "Cobrar" funciona aunque el cliente no la haya pedido (cuando todo está entregado). Puedes cambiar la forma de pago; en efectivo escribe lo recibido y ves el vuelto; con Yape o Plin muestra el QR y anota el código de operación; con "Dividir el pago (mixto)" repartes entre varias formas (lo que sobra en efectivo es vuelto).',
  },
  {
    title: "Descuento, ticket y WhatsApp",
    description:
      '"Aplicar descuento o cortesía" (S/ o %, con motivo, queda registrado a tu nombre). Imprime el ticket o envíalo por WhatsApp si el cliente tiene teléfono. En delivery, el envío ya va sumado.',
  },
  {
    title: "Boleta o factura (modo prueba)",
    description:
      'Si el administrador activó la facturación, al cobrar eliges "Nota de venta", "Boleta" o "Factura". Para factura, la cuenta debe tener un cliente con RUC; para una boleta desde S/ 700, un cliente con DNI. Abajo hay dos interruptores: "Venta por consumo" (el comprobante lleva una sola línea y no lista los productos; arranca apagado) y "Descargar PDF al cobrar" (baja el PDF solo; recuerda tu última elección). Al emitirlo puedes imprimirlo o descargarlo en PDF. Hoy es una prueba: el comprobante dice "DOCUMENTO DE PRUEBA" y no se envía a SUNAT. Caja → Comprobantes lista los emitidos (con PDF y exportación a Excel).',
  },
  {
    title: "Clientes con RUC o DNI",
    description:
      'Al registrar un cliente con RUC el sistema revisa que el número sea válido. Elige el documento (DNI, RUC o sin documento) y escribe el número: si es válido y el negocio tiene la consulta activada, la razón social y la dirección se llenan solas (también puedes usar el botón "Consultar").',
  },
  {
    title: "Al cobrar, la mesa queda libre",
    description: "Sale de la lista de Cobrar y el mozo la ve libre en Pedidos. El cliente vuelve a su pantalla de inicio.",
  },
  {
    title: "¿Se fueron sin pagar?",
    description:
      '"Liberar mesa": lo servido o preparado queda como pérdida y la mesa se libera.',
  },
  {
    title: "Reparte las propinas antes de cerrar",
    description:
      'Si hubo propinas, en Apertura y cierre → «Propinas» marca quiénes participan y toca "Calcular reparto": verás cuánto le toca a cada uno según la regla del restaurante. "Liquidar" registra un egreso por persona (sale del cajón); entrega a cada uno su parte. Tocarlo dos veces no paga dos veces. Cada liquidación se puede imprimir (botón de impresora) con una columna para la firma de cada persona. En Caja → Liquidación de propinas están todas, por fechas, con el total por persona y descarga a Excel. Si te equivocaste, el administrador puede anular la liquidación mientras la caja siga abierta.',
  },
  {
    title: "Cierra la caja al terminar",
    description:
      "Cuenta el efectivo del cajón: el sistema te dice cuánto debería haber y la diferencia. Si quedan propinas sin repartir, primero hay que liquidarlas. Los cierres anteriores están en Cierres de caja.",
  },
];

const kitchenSteps = [
  {
    title: "Abre Cocina en la tablet del área",
    description:
      'Menú ☰ → Cocina, elige tu área (Cocina, Bar…); puedes elegir varias a la vez (ej. Cocina + Ceviches) o dejarlo vacío para ver todas. Toca "Activar sonido" y "Pantalla completa".',
  },
  {
    title: "Prepara por orden de llegada",
    description:
      "Cada tarjeta es una comanda de una mesa, con las notas resaltadas. El borde pasa de verde a amarillo (10 min) y a rojo (20 min).",
  },
  {
    title: 'Toca "Listo" al terminar cada plato',
    description: "Sale de tu pantalla y el mozo ve la mesa en morado para llevarlo.",
  },
  {
    title: "Tachado en rojo = cancelado",
    description: "El mozo lo canceló: deja de prepararlo.",
  },
];

// Pedidos que llegan por WhatsApp o delivery (no los recibe el sistema: alguien
// del local los registra). Ver reglas #26 y #30 y "Entregar exige el Listo".
const whatsappSteps = [
  {
    title: "Llega el pedido por WhatsApp",
    description:
      'iCard no lee el chat: alguien del local lo registra. Menú ☰ → "Venta rápida y delivery" y abre "WhatsApp 1" (o el delivery o la venta rápida que corresponda).',
  },
  {
    title: "Añade los platos",
    description:
      '"Añadir pedido". Escribe en la nota de cada plato lo que pidió el cliente (ej. "sin ají"). Todo viene marcado "Para llevar". En delivery elige el canal (PedidosYa, Rappi, Teléfono…) y el costo de envío.',
  },
  {
    title: "Cocina y Bar lo preparan",
    description:
      'Cada área ve sus platos en su pantalla y marca "Listo". En la mesa verás "4 de 6 listos · faltan 2 por preparar".',
  },
  {
    title: "Todo sale junto, en una bolsa",
    description:
      'Cuando todo está listo, la mesa muestra "Los 6 están listos" y aparece "Entregar todo". Se entrega el pedido completo, no plato por plato.',
  },
  {
    title: "Entrega cerca de la Caja y cobra",
    description:
      'El mozo lleva el pedido junto a la Caja. La Caja cobra en Caja → Cobrar (efectivo, tarjeta, Yape o Plin con su código de operación) y el cliente se lo lleva. Si come en el local, se trata igual.',
  },
  {
    title: "¿Dónde se ve cuánto se vende?",
    description:
      'En el Panel del día, "Por canal de venta" separa Salón, Venta rápida, WhatsApp y Delivery (y el delivery por app); con dos o más ambientes, "Por ambiente" separa las ventas del salón entre Salón, Terraza… Estas ventas no cuentan en "Comensales atendidos".',
  },
];

const adminSteps = [
  {
    title: "Al entrar, el Panel del día",
    description:
      "El Admin empieza en el resumen de ventas del día; el mozo y el cajero, en Pedidos. Todo lo demás está en el menú ☰.",
  },
  {
    title: "Crea los usuarios con su rol",
    description:
      'Configuración → Usuarios. Mozo = Estándar. Cajero = Estándar con "Puede cobrar (caja)". Administrador = Admin. Cada uno ve en el menú solo lo de su trabajo. Todo trabajador se crea con su "Puesto" (Mesero, Cocina, Bar, Caja u Otro): no da permisos, sirve para repartir propinas. Quien se registra pero no entra al sistema (ej. limpieza) se crea con "Puede iniciar sesión" apagado: no necesita correo ni contraseña. Para elegir al mesero de una mesa aparecen todos menos el puesto "Otro". Al editar un usuario, deja la contraseña vacía para no cambiarla.',
  },
  {
    title: "Facturación (modo prueba)",
    description:
      'Configuración → Facturación: los datos del negocio como emisor (RUC, razón social, dirección) y las series de boleta (B001…) y de factura (F001…). El número avanza solo y nunca se repite; una serie creada no se edita. Los comprobantes son simulados hasta conectar un proveedor real.',
  },
  {
    title: "Propinas",
    description:
      'Configuración → Propinas: las activas con "Registrar propinas" y eliges cómo se reparten — Individual (cada mesero lo de sus mesas), Pozo equitativo (partes iguales) Puntos por puesto (los puntos son el peso de cada persona; con todos en 1 es partes iguales) o Porcentaje por puesto (ej. 75 % para los meseros y el resto para cocina, bar, caja y otros; cada puesto lo reparte en partes iguales entre los suyos) —, si se descuenta la comisión de la tarjeta, una retención opcional y si la de tarjeta se paga desde la caja o queda por pagar (transferencia o planilla; ahí mismo la marcas "Pagada"). Cambiar la regla no altera lo ya liquidado.',
  },
  {
    title: "¿Cobraste mal una cuenta?",
    description:
      'Historial de Pagos → botón rojo "Anular" (solo Admin), con el motivo. La venta deja de contar y queda en Reportes → Anulaciones y descuentos.',
  },
  {
    title: "Panel del día y Reportes",
    description:
      "Ventas, ticket promedio, ventas por ambiente (con dos o más), horas pico, ranking de mozos, comensales y ticket por persona, tiempos de cocina, pérdidas, anulaciones y descuentos (quién canceló, anuló o descontó qué y por qué). Todo se puede imprimir. El día de trabajo termina a las 5:00 AM: lo vendido de madrugada cuenta en la noche anterior.",
  },
  {
    title: "Carta",
    description:
      "Categorías (con su área de producción, para que lleguen a la cocina correcta) y productos.",
  },
  {
    title: "Mesas y QR",
    description:
      'Ambientes: en Configuración → Mesas agrega los espacios del local (Salón, Terraza, Piso 2…), renómbralos y ordénalos; ese orden es el de las pestañas. Cada ambiente numera sus mesas desde 1 (Salón · Mesa 1 y Terraza · Mesa 1 son distintas) y, si quitas una mesa, las demás conservan su número. Con un solo ambiente su nombre no aparece en pantalla. Un ambiente con mesas no se puede borrar: primero muévelas. Al mover una mesa de ambiente, reimprime su número; el QR sigue sirviendo. Cada QR lleva un código. Si usas "Regenerar código", reimprime el QR de esa mesa. Venta rápida / WhatsApp / Delivery: crea mesas con ese tipo, un número interno (ej. 101) y un nombre ("Venta rápida 1"); no llevan QR.',
  },
  {
    title: "Configuración",
    description:
      "Clientes, usuarios y roles (marca \"Puede cobrar (caja)\" al cajero si es Estándar), QR de Yape/Plin para cobrar, y el logo y nombre del negocio (aparecen en el menú ☰, la pantalla del cliente y los tickets). La información se respalda sola cada noche.",
  },
  {
    title: "Instálala como app",
    description:
      'En Chrome, botón "Instalar"; en el celular, "Agregar a pantalla de inicio". Queda con el nombre y logo del restaurante.',
  },
];

function ColorDot(props) {
  const { color } = props;
  return (
    <span
      style={{
        display: "inline-block",
        width: 14,
        height: 14,
        borderRadius: "50%",
        backgroundColor: color,
        marginRight: 8,
        verticalAlign: "middle",
      }}
    />
  );
}

// Secciones plegables; `mine` es la que arranca abierta según el rol.
const sections = [
  { key: "worker", title: "Mozo", steps: workerSteps },
  { key: "cashier", title: "Caja", steps: cashierSteps },
  { key: "kitchen", title: "Cocina", steps: kitchenSteps },
  { key: "whatsapp", title: "Pedidos por WhatsApp y delivery", steps: whatsappSteps },
  { key: "admin", title: "Administración", steps: adminSteps },
  { key: "client", title: "Cliente", steps: clientSteps },
];

const normalize = (text) =>
  (text || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

function StepList(props) {
  const { steps } = props;
  return (
    <List ordered relaxed>
      {steps.map((step) => (
        <List.Item key={step.title}>
          <List.Content>
            <List.Header>{step.title}</List.Header>
            <List.Description>{step.description}</List.Description>
          </List.Content>
        </List.Item>
      ))}
    </List>
  );
}

export function HelpAdmin() {
  const { auth } = useAuth();
  const me = auth?.me;
  const [query, setQuery] = useState("");
  // Acordeón: una sola sección abierta a la vez (null = ninguna; undefined = la de mi rol).
  const [openKey, setOpenKey] = useState(undefined);
  const q = normalize(query.trim());

  const visible = sections
    .map((sec) => ({
      ...sec,
      steps: q
        ? sec.steps.filter((st) => normalize(`${st.title} ${st.description}`).includes(q))
        : sec.steps,
    }))
    .filter((sec) => sec.steps.length > 0);

  const mine = me?.is_staff ? "admin" : canCharge(me) ? "cashier" : "worker";
  // Con búsqueda se abren todas las que tienen resultados.
  const isOpen = (sec) => !!q || sec.key === (openKey === undefined ? mine : openKey);

  return (
    <>
      <HeaderPage title="Ayuda" />

      <Input
        fluid
        icon="search"
        iconPosition="left"
        placeholder="Buscar en la Ayuda (ej. propina, anular, Yape, descuento)…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        style={{ marginBottom: 14 }}
      />

      {!q && (
      <Segment>
        <h4>El menú ☰</h4>
        {menuItems.map((item) => (
          <div key={item.title} style={{ display: "flex", gap: 10, marginBottom: 12 }}>
            <span style={{ flex: "0 0 24px", textAlign: "center", color: "#e67e22" }}>
              <Icon name={item.icon} />
            </span>
            <div>
              <strong>{item.title}</strong>
              <div style={{ color: "#555" }}>{item.description}</div>
            </div>
          </div>
        ))}
      </Segment>
      )}

      {!q && (
      <Segment>
        <h4>Quién ve qué</h4>
        <p>El mozo atiende las mesas; la Caja cobra y las deja libres.</p>
        <Grid stackable columns={2}>
          {roles.map((role) => (
            <Grid.Column key={role.title}>
              <div style={{ display: "flex", gap: 10 }}>
                <span style={{ flex: "0 0 24px", textAlign: "center", color: "#e67e22" }}>
                  <Icon name={role.icon} size="large" />
                </span>
                <div>
                  <strong>{role.title}</strong>{" "}
                  <span style={{ color: "#888" }}>— {role.who}</span>
                  <div style={{ color: "#555" }}>
                    <em>Ve:</em> {role.sees}
                  </div>
                  <div style={{ color: "#555" }}>{role.does}</div>
                </div>
              </div>
            </Grid.Column>
          ))}
        </Grid>
      </Segment>
      )}

      {!q && (
      <Segment>
        <h4>Reconoce el color de la mesa</h4>
        <p>De un vistazo, así sabes en qué está cada mesa sin entrar a revisarla.</p>
        <div style={{ display: "flex", gap: "20px", flexWrap: "wrap" }}>
          <Label size="large" basic>
            <ColorDot color="#0094e3" />
            Celeste — hay pedidos en cocina
          </Label>
          <Label size="large" basic>
            <ColorDot color="#8e44ad" />
            Morado — hay platos listos para llevar
          </Label>
          <Label size="large" basic>
            <ColorDot color="#f1c40f" />
            Amarillo — ya se entregó todo
          </Label>
          <Label size="large" basic>
            <ColorDot color="#008000" />
            Verde — pidieron la cuenta
          </Label>
        </div>
      </Segment>
      )}

      {visible.length === 0 && <p style={{ color: "#888" }}>No encontramos nada con «{query}».</p>}

      {visible.map((sec) => (
        <Segment key={sec.key}>
          <h3
            style={{ cursor: "pointer", margin: 0 }}
            onClick={() => setOpenKey(isOpen(sec) ? null : sec.key)}
          >
            <Icon name={isOpen(sec) ? "angle down" : "angle right"} />
            {sec.title}
          </h3>
          {isOpen(sec) && (
            <div style={{ marginTop: 12 }}>
              <StepList steps={sec.steps} />
            </div>
          )}
        </Segment>
      ))}

      {/* Para saber qué versión corre cada instalación (local, demo, web):
          el SHA lo pone GitHub Actions al compilar (REACT_APP_VERSION). */}
      <p style={{ marginTop: 24, color: "#999", fontSize: "0.9rem" }}>
        Versión {process.env.REACT_APP_VERSION || "dev"} · {window.location.host}
      </p>
    </>
  );
}
